import { isBrowser } from 'ws-scripts/modules/environment';

/**
 * Returns `window[type]` if it's safe to access, or `null` otherwise. Never throws.
 *
 * @param {string} type Either 'sessionStorage' or 'localStorage'.
 * @returns {Storage|null}
 */
const getStorage = (type) => {
	if (!isBrowser) {
		return null;
	}
	try {
		return window[type] || null;
	} catch (e) {
		// Accessing the storage object itself can throw (e.g. SecurityError).
		return null;
	}
};

/**
 * Safely reads a value from browser storage. Returns `null` if storage is
 * unavailable, missing, or the read throws for any reason.
 *
 * @param {string} type Either 'sessionStorage' or 'localStorage'.
 * @param {string} key
 * @returns {string|null}
 */
export const safeGetItem = (type, key) => {
	try {
		return getStorage(type)?.getItem(key) ?? null;
	} catch (e) {
		return null;
	}
};

/**
 * Safely writes a value to browser storage. Fails silently (returns `false`)
 * if storage is unavailable or the write throws (e.g. quota exceeded).
 *
 * @param {string} type Either 'sessionStorage' or 'localStorage'.
 * @param {string} key
 * @param {string} value
 * @returns {boolean} Whether the write succeeded.
 */
export const safeSetItem = (type, key, value) => {
	try {
		const storage = getStorage(type);
		if (!storage) {
			return false;
		}
		storage.setItem(key, value);
		return true;
	} catch (e) {
		return false;
	}
};

/**
 * Safely removes a value from browser storage. Fails silently (returns
 * `false`) if storage is unavailable or the removal throws.
 *
 * @param {string} type Either 'sessionStorage' or 'localStorage'.
 * @param {string} key
 * @returns {boolean} Whether the removal succeeded.
 */
export const safeRemoveItem = (type, key) => {
	try {
		const storage = getStorage(type);
		if (!storage) {
			return false;
		}
		storage.removeItem(key);
		return true;
	} catch (e) {
		return false;
	}
};
